import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '../../components/ui/button'
import { getLocalApiUrl, authFetch } from '../../config/api'

function fmtWhen(iso) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('en-AU', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return String(iso)
  }
}

export default function FormRecords() {
  const { formId } = useParams()
  const navigate = useNavigate()
  const [forms, setForms] = useState([])
  const [selectedId, setSelectedId] = useState(formId || '')
  const [form, setForm] = useState(null)
  const [rows, setRows] = useState([])
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const loadForms = useCallback(async () => {
    const res = await authFetch(getLocalApiUrl('/api/forms'))
    const j = await res.json()
    if (!res.ok || !j.success) throw new Error(j.error || 'Failed to load forms')
    setForms(j.data || [])
    return j.data || []
  }, [])

  const loadRecords = useCallback(async (id, search) => {
    if (!id) {
      setForm(null)
      setRows([])
      return
    }
    setLoading(true)
    setError('')
    try {
      const qs = search ? `?q=${encodeURIComponent(search)}` : ''
      const res = await authFetch(getLocalApiUrl(`/api/forms/${id}/submissions${qs}`))
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Failed to load records')
      setForm(j.data?.form || null)
      setRows(j.data?.submissions || [])
    } catch (e) {
      setError(e.message)
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadForms()
      .then((list) => {
        if (formId) return
        const first = list.find((f) => (f.submissionCount || 0) > 0) || list[0]
        if (first) {
          setSelectedId(first.id)
          navigate(`/compliance-safe/forms/${first.id}/records`, { replace: true })
        }
      })
      .catch((e) => setError(e.message))
  }, [formId, loadForms, navigate])

  useEffect(() => {
    if (formId) setSelectedId(formId)
  }, [formId])

  useEffect(() => {
    if (formId) loadRecords(formId, q)
  }, [formId, q, loadRecords])

  const onPick = (id) => {
    setSelectedId(id)
    setQ('')
    if (id) navigate(`/compliance-safe/forms/${id}/records`)
  }

  const previewKeys = []
  for (const row of rows) {
    for (const p of row.preview || []) {
      if (!previewKeys.includes(p.label)) previewKeys.push(p.label)
    }
    if (previewKeys.length >= 3) break
  }

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Form records</h1>
          <p className="text-sm text-slate-500 mt-1">
            Select a form to see every submitted record. Open a row for the full answers.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate('/compliance-safe/forms')}>Forms</Button>
          <Button variant="outline" onClick={() => navigate('/compliance-safe/forms/pipeline')}>Pipeline</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="block min-w-[240px] flex-1">
          <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Form</span>
          <select
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
            value={selectedId}
            onChange={(e) => onPick(e.target.value)}
          >
            <option value="">Select a form…</option>
            {forms.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
                {f.facing === 'patron' ? ' · Patron' : ' · Internal'}
                {` · ${f.submissionCount || 0} submitted`}
              </option>
            ))}
          </select>
        </label>
        <label className="block w-64">
          <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Search</span>
          <input
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            placeholder="Ref, patron, or submitter"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            disabled={!formId}
          />
        </label>
      </div>

      {form ? (
        <div className="text-sm text-slate-500 mb-3">
          {form.facing === 'patron' ? 'Patron send' : 'Internal assign'}
          {form.currentVersionNo ? ` · published v${form.currentVersionNo}` : ''}
          {` · ${rows.length} record${rows.length === 1 ? '' : 's'}`}
        </div>
      ) : null}

      {error ? <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}
      {loading ? <div className="text-sm text-slate-500">Loading…</div> : null}

      {!formId && !loading ? (
        <div className="text-sm text-slate-500">Choose a form name to load its submitted records.</div>
      ) : null}

      {formId && !loading && !rows.length && !error ? (
        <div className="text-sm text-slate-500">No submitted records for this form yet.</div>
      ) : null}

      {rows.length ? (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-400">
                <th className="px-4 py-2.5 font-semibold">Ref</th>
                <th className="px-4 py-2.5 font-semibold">Submitted</th>
                <th className="px-4 py-2.5 font-semibold">Subject</th>
                <th className="px-4 py-2.5 font-semibold">By</th>
                <th className="px-4 py-2.5 font-semibold">Status</th>
                {previewKeys.map((k) => (
                  <th key={k} className="px-4 py-2.5 font-semibold">{k}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const previewMap = Object.fromEntries((r.preview || []).map((p) => [p.label, p.value]))
                return (
                  <tr
                    key={r.id}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/compliance-safe/forms/assignments/${r.assignmentId}`)}
                  >
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-800">{r.ref}</td>
                    <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{fmtWhen(r.submittedAt)}</td>
                    <td className="px-4 py-2.5 text-slate-800">
                      {r.subjectLabel || r.subjectType || '—'}
                      {r.isTest ? <span className="ml-1 text-[10px] font-semibold uppercase text-amber-700">Test</span> : null}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">{r.submittedByName || '—'}</td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-semibold text-slate-700">
                        {r.status}
                      </span>
                    </td>
                    {previewKeys.map((k) => (
                      <td key={k} className="px-4 py-2.5 text-slate-600 max-w-[180px] truncate">{previewMap[k] || '—'}</td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  )
}
